import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { allergenListQueryOptions } from '@/api/queries/allergen';
import { cuisineListQueryOptions } from '@/api/queries/cuisine';
import { dietaryProfileListQueryOptions } from '@/api/queries/dietary-profile';
import {
  ONBOARDING_ALLERGEN_SLUGS,
  ONBOARDING_CUISINE_SLUGS,
  ONBOARDING_DIETARY_SLUGS,
} from '@/features/onboarding/constants';
import type { TasteRef } from '@/types/taste-profile';

/**
 * The reference lists are curated and small (tens of rows), but the gateway
 * pages them at 20 by default. Ask for the whole table in one page so a
 * shortlisted slug can never be silently paged out of the response.
 */
const REFERENCE_PAGE_SIZE = 100;

/**
 * What a consumer shows in place of a facet that failed to load. Shared so the
 * setup step and the existing-user prompt say the same thing.
 */
export const FACET_LOAD_ERROR = 'Couldn’t load these right now - you can set them in Settings later';

/** The shape every reference row shares; enough to build a TasteRef. */
interface ReferenceRow {
  id: string;
  slug: string;
  name: string;
}

/**
 * Resolve a curated slug shortlist against the reference rows the server sent.
 *
 * The result follows SHORTLIST order (the API orders by its own rules), and a
 * slug with no matching row is dropped rather than rendered as a broken chip.
 */
function resolveShortlist(
  rows: readonly ReferenceRow[] | undefined,
  slugs: readonly string[]
): TasteRef[] {
  const bySlug = new Map((rows ?? []).map((row) => [row.slug, row]));
  return slugs.flatMap((slug) => {
    const row = bySlug.get(slug);
    return row ? [{ id: row.id, slug: row.slug, name: row.name }] : [];
  });
}

export interface SetupFacetErrors {
  cuisines: boolean;
  allergens: boolean;
  dietaryProfiles: boolean;
}

export interface SetupFacetOptions {
  cuisines: TasteRef[];
  allergens: TasteRef[];
  dietaryProfiles: TasteRef[];
  isLoading: boolean;
  /** True when any facet failed to load. */
  isError: boolean;
  /** Per-facet failure flags, so one dead facet does not blank the others. */
  errors: SetupFacetErrors;
}

/**
 * The onboarding/setup taste shortlists, resolved from live reference data.
 *
 * Curation lives in `@/features/onboarding/constants` as slugs; the ids come
 * from the server on every render path, which is what keeps the shortlist from
 * drifting away from the rows the foreign keys point at.
 */
export function useSetupFacetOptions(): SetupFacetOptions {
  const cuisineQuery = useQuery(cuisineListQueryOptions({ per_page: REFERENCE_PAGE_SIZE }));
  const allergenQuery = useQuery(allergenListQueryOptions({ per_page: REFERENCE_PAGE_SIZE }));
  const dietaryQuery = useQuery(dietaryProfileListQueryOptions({ per_page: REFERENCE_PAGE_SIZE }));

  const cuisines = useMemo(
    () => resolveShortlist(cuisineQuery.data?.items, ONBOARDING_CUISINE_SLUGS),
    [cuisineQuery.data]
  );
  const allergens = useMemo(
    () => resolveShortlist(allergenQuery.data?.items, ONBOARDING_ALLERGEN_SLUGS),
    [allergenQuery.data]
  );
  const dietaryProfiles = useMemo(
    () => resolveShortlist(dietaryQuery.data?.items, ONBOARDING_DIETARY_SLUGS),
    [dietaryQuery.data]
  );

  const errors = useMemo(
    () => ({
      cuisines: cuisineQuery.isError,
      allergens: allergenQuery.isError,
      dietaryProfiles: dietaryQuery.isError,
    }),
    [cuisineQuery.isError, allergenQuery.isError, dietaryQuery.isError]
  );

  return {
    cuisines,
    allergens,
    dietaryProfiles,
    isLoading: cuisineQuery.isLoading || allergenQuery.isLoading || dietaryQuery.isLoading,
    isError: errors.cuisines || errors.allergens || errors.dietaryProfiles,
    errors,
  };
}
